import DataGrid from '../common/DataGrid';
import React, {Fragment, useState} from 'react';
import {Box, Typography} from '@mui/material';
import {DateTime} from 'luxon';
import {useGetPolicyAuditLog} from '@account/api/archiveControl';
import {useParams} from 'react-router-dom';

const scalarToAuditString = value => {
  const maybeDate = DateTime.fromHTTP(value, {zone: 'utc'});

  if (value === null || value === undefined) {
    return '—';
  }
  if (typeof value === 'object' && !Array.isArray(value)) {
    try {
      return JSON.stringify(value);
    } catch {
      return String(value);
    }
  }
  if (maybeDate.isValid) {
    return maybeDate.toFormat('fff');
  }

  return String(value);
};

const normalizeChangeset = changeset => {
  const keys = Object.keys(changeset);

  return keys.reduce(
    (accumulator, key) => {
      const [oldVal, newVal] = changeset[key];

      if (['excluded_organization_ids', 'excluded_user_ids'].includes(key)) {
        accumulator.oldValues[key] = oldVal ? `${oldVal?.length} items` : '--';
        accumulator.newValues[key] = newVal ? `${newVal?.length} items` : '--';
      } else {
        accumulator.oldValues[key] = scalarToAuditString(oldVal);
        accumulator.newValues[key] = scalarToAuditString(newVal);
      }

      return accumulator;
    },
    {
      oldValues: {},
      newValues: {},
    }
  );
};

const columns = [
  {
    field: 'created_at',
    headerName: 'Edited at',
    flex: 0.5,
    sortable: false,
    valueGetter: params =>
      DateTime.fromHTTP(params.value, {zone: 'utc'}).toFormat('fff'),
  },
  {field: 'changed_by', headerName: 'Edited by', flex: 0.5, sortable: false},
  {
    field: 'changeset_old',
    headerName: 'Old state',
    flex: 0.5,
    sortable: false,
    valueGetter: params => params.row.changeset,
    renderCell: params => {
      const {oldValues} = normalizeChangeset(params.value);
      return (
        <Box component="dl" sx={{width: '100%'}}>
          {Object.keys(oldValues).map(prop => (
            <Fragment key={prop}>
              <Typography
                variant="subtitle2"
                component="dt"
                noWrap
                title={prop}
              >
                {prop}
              </Typography>
              <Typography
                variant="caption"
                component="dd"
                noWrap
                title={oldValues[prop]}
              >
                {oldValues[prop]}
              </Typography>
            </Fragment>
          ))}
        </Box>
      );
    },
  },
  {
    field: 'changeset_new',
    headerName: 'New state',
    flex: 0.5,
    sortable: false,
    valueGetter: params => params.row.changeset,
    renderCell: params => {
      const {newValues} = normalizeChangeset(params.value);
      return (
        <Box component="dl" sx={{width: '100%'}}>
          {Object.keys(newValues).map(prop => (
            <Fragment key={prop}>
              <Typography variant="subtitle2" component="dt" title={prop}>
                {prop}
              </Typography>
              <Typography
                variant="caption"
                component="dd"
                noWrap
                title={newValues[prop]}
              >
                {newValues[prop]}
              </Typography>
            </Fragment>
          ))}
        </Box>
      );
    },
  },
];

const PolicyAuditLog = () => {
  const {policyId} = useParams();
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 5,
  });

  const auditLogQuery = useGetPolicyAuditLog(
    {
      policyId,
      params: {
        limit: paginationModel.pageSize,
        offset: paginationModel.pageSize * paginationModel.page,
      },
    },
    {
      enabled: !!policyId,
      keepPreviousData: true,
    }
  );

  return (
    <DataGrid
      disableColumnSorting
      columns={columns}
      loading={auditLogQuery.isFetching}
      rows={auditLogQuery?.data?.results ?? []}
      rowCount={auditLogQuery?.data?.meta?.count ?? 0}
      getRowId={row => DateTime.fromHTTP(row.created_at).valueOf()}
      getRowHeight={() => 'auto'}
      pageSizeOptions={[5, 10, 25]}
      pagination
      paginationMode="server"
      paginationModel={paginationModel}
      onPaginationModelChange={setPaginationModel}
    />
  );
};

export default PolicyAuditLog;
